import type { UseFormReturn } from 'react-hook-form';
import type { EditCardFormValues } from './types';
import { useTranslation } from 'react-i18next';
import { useUpdateCard, useDeleteCard } from '../api';
import { useToast } from '@/shared/lib';
import { useCallback } from 'react';
import type { ConfirmationConfig } from '@/widgets';

type Props = {
  form: UseFormReturn<EditCardFormValues>;
  templateId: string;
  onSuccess: () => void;
  openConfirmationModal: (config: ConfirmationConfig) => void;
};

type Params = {
  updateCard: () => Promise<void>;
  deleteCard: () => Promise<void>;
  isPending: boolean;
};

export const useEditCardFormLogic = ({
  form,
  templateId,
  onSuccess,
  openConfirmationModal,
}: Props): Params => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { mutateAsync: updateCardAsync, isPending } = useUpdateCard({
    onSuccess: () => {
      toast.success(t('savedAddresses.card.editing.result.success'));
      onSuccess();
    },
    onError: error => {
      toast.error(
        error.message || t('savedAddresses.card.editing.result.error')
      );
    },
  });

  const { mutateAsync: deleteCardAsync } = useDeleteCard({
    onSuccess: () => {
      toast.success(t('savedAddresses.card.deletion.result.success'));
      onSuccess();
    },
    onError: error => {
      toast.error(
        error.message || t('savedAddresses.card.deletion.result.error')
      );
    },
  });

  const updateCard = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    await updateCardAsync({
      templateId,
      data: {
        cardholderName: form.getValues('cardholderName'),
        templateName: form.getValues('templateName'),
      },
    });
  }, [form, templateId, updateCardAsync]);

  const deleteCard = useCallback(async () => {
    openConfirmationModal({
      title: t('savedAddresses.card.deletion.title'),
      description: t('savedAddresses.card.deletion.description'),
      confirmText: t('savedAddresses.card.deletion.confirmText'),
      cancelText: t('savedAddresses.card.deletion.cancelText'),
      onConfirm: async () =>
        await deleteCardAsync({
          templateId,
        }),
    });
  }, [templateId, deleteCardAsync, openConfirmationModal, t]);

  return {
    updateCard,
    deleteCard,
    isPending,
  };
};
